import type { signInStartRequestSchema } from "@esi/shared";
import type { z } from "zod";
import { api } from "../api.ts";
import { flowHeaders } from "./pre-session.ts";

/**
 * Whose sign-in page this is (openspec change operator-identity-pool, D2): the tenant
 * application's (`/api/auth`) or the console website's (`/api/auth/operator`, operators only,
 * no IdP redirect, no passkey, no link or reset route).
 */
export type SignInRealm = "tenant" | "operator";

/** The pre-session calls of one realm's sign-in page, with the flow's CSRF header. */
export function signInApi(realm: SignInRealm) {
  if (realm === "operator") {
    const auth = api.api.auth.operator;
    return {
      csrf: () => auth.csrf.$post(),
      start: (json: { email: string; returnTo: string }) =>
        auth.start.$post({ json }, flowHeaders()),
      password: (json: { password: string }) => auth.password.$post({ json }, flowHeaders()),
      challenge: (
        json: { type: "totp"; code: string } | { type: "new_password"; password: string },
      ) => auth.challenge.$post({ json }, flowHeaders()),
      enrolTotp: () => auth.enrol.totp.$post(undefined, flowHeaders()),
      enrolTotpVerify: (json: { code: string }) =>
        auth.enrol.totp.verify.$post({ json }, flowHeaders()),
      /** No passkey on the console in this release: never offered, so never called. */
      passkey: null,
    };
  }
  const auth = api.api.auth;
  return {
    csrf: () => auth.csrf.$post(),
    /**
     * `method` (#419, D6): `choice` asks for the choice step on a domain whose mode allows ESI
     * credentials; `credentials` is the person's pick there. A release before ignores it.
     */
    start: (
      json: Omit<z.infer<typeof signInStartRequestSchema>, "returnTo"> & { returnTo: string },
    ) => auth.start.$post({ json }, flowHeaders()),
    password: (json: { password: string }) => auth.password.$post({ json }, flowHeaders()),
    challenge: (
      json: { type: "totp"; code: string } | { type: "new_password"; password: string },
    ) => auth.challenge.$post({ json }, flowHeaders()),
    enrolTotp: () => auth.enrol.totp.$post(undefined, flowHeaders()),
    enrolTotpVerify: (json: { code: string }) =>
      auth.enrol.totp.verify.$post({ json }, flowHeaders()),
    passkey: {
      options: () => auth.passkey.options.$post(undefined, flowHeaders()),
      assert: (json: { credential: Record<string, unknown> }) =>
        auth.passkey.$post({ json }, flowHeaders()),
      enrolOptions: () => auth.enrol.passkey.options.$post(undefined, flowHeaders()),
      enrol: (json: { credential: Record<string, unknown> }) =>
        auth.enrol.passkey.$post({ json }, flowHeaders()),
    },
  };
}
